import React from "react";

import { IconButton, Stack, TooltipHost, useTheme } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { PatientCardIds } from "@modules/practice/screens/shared-components/types/patient-card-ids.enum.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { When } from "@ui-components/withPerm.tsx";

import { getPatientSearchBoxStyles } from "./PatientAppointmentsSearch.styles.ts";

interface ActionButtonsProps {
  patient: Contact;
  onIconClick: (patientId: string) => void;
}
export const PatientSearchActionButtons: React.FC<ActionButtonsProps> = ({
  patient,
  onIconClick
}) => {
  const { practice, routing, core } = useStores();
  const theme = useTheme();
  const { actionButtonsStyles } = getPatientSearchBoxStyles(theme);

  const onRecordUpdateClick = (patientId: string) => {
    routing.pushWithFromQuery(
      routes.records.recordUpdate.path({ id: patientId })
    );
    onIconClick(patientId);
  };

  const onViewRecordClick = (patientId: string) => {
    routing.pushWithFromQuery(
      routes.records.recordView.path({ id: patientId })
    );
    onIconClick(patientId);
  };

  const handleOnEditIconClick = (patientId: string) => {
    practice.ui.showEditContact(PatientCardIds.patientHeader, patientId);
    onIconClick(patientId);
  };

  const showViewRecord =
    core.hasPermissions(Permission.EncounterRead) &&
    !core.hasPermissions(Permission.EncounterWrite);

  const showRecordUpdate =
    core.hasPermissions(Permission.EncounterRead) &&
    core.hasPermissions(Permission.EncounterWrite);

  return (
    <Stack horizontal verticalAlign="center" styles={actionButtonsStyles}>
      {showViewRecord && (
        <TooltipHost content="View clinical record">
          <IconButton
            iconProps={{ iconName: "ContactCard" }}
            onClick={() => onViewRecordClick(patient.id)}
            styles={{ icon: { height: "17px" } }}
          />
        </TooltipHost>
      )}

      {showRecordUpdate && (
        <TooltipHost content="Record update">
          <IconButton
            iconProps={{ iconName: "NewFolder" }}
            onClick={() => onRecordUpdateClick(patient.id)}
            styles={{ icon: { height: "17px" } }}
          />
        </TooltipHost>
      )}

      <When permission={[Permission.PatientWrite]}>
        <TooltipHost content="Edit demographics">
          <IconButton
            iconProps={{ iconName: "Edit" }}
            onClick={() => handleOnEditIconClick(patient.id)}
            styles={{ icon: { fontSize: "15px" } }}
          />
        </TooltipHost>
      </When>
    </Stack>
  );
};
